import type { Map as MapLibreMap } from 'maplibre-gl';

// SVG paths authored in 28×28 coordinate space
const SVG_EDUCATION = 'M14,4 L4,10 L4,24 L10,24 L10,16 L18,16 L18,24 L24,24 L24,10 Z';
const SVG_HEALTH =
  'M11,4 L11,11 L4,11 L4,17 L11,17 L11,24 L17,24 L17,17 L24,17 L24,11 L17,11 L17,4 Z';
const SVG_RETAIL = 'M6,8 L6,22 L22,22 L22,8 L19,8 L19,5 L9,5 L9,8 Z';
const SVG_FUEL = 'M8,4 L8,20 L6,20 L6,22 L18,22 L18,20 L16,20 L16,12 L20,12 L20,8 L16,8 L16,4 Z';
const SVG_RAIL = 'M8,4 L8,18 L5,22 L7,22 L9,20 L15,20 L17,22 L19,22 L16,18 L16,4 Z';
const SVG_AIRPLANE =
  'M14,4 L14,16 L22,20 L22,18 L16,14 L16,10 L22,12 L22,10 L14,6 L6,10 L6,12 L12,10 L12,14 L6,18 L6,20 L14,16 Z';
const SVG_BANK = 'M6,6 L6,22 L22,22 L22,6 Z M10,12 L14,12 L14,10 L18,10 L18,14 L10,14 Z';
const SVG_PARK =
  'M14,4 C10,4 6,8 6,12 C6,16 10,18 12,18 L12,24 L16,24 L16,18 C18,18 22,16 22,12 C22,8 18,4 14,4 Z';
const SVG_STAR = 'M14,4 L16,10 L22,10 L17,14 L19,20 L14,16 L9,20 L11,14 L6,10 L12,10 Z';
const SVG_WORSHIP =
  'M13,4 L13,12 L8,12 L8,14 L13,14 L13,24 L15,24 L15,14 L20,14 L20,12 L15,12 L15,4 Z';
const SVG_FALLBACK =
  'M14,4 C10,4 7,7 7,11 C7,17 14,24 14,24 C14,24 21,17 21,11 C21,7 18,4 14,4 Z M14,9 C12.3,9 11,10.3 11,12 C11,13.7 12.3,15 14,15 C15.7,15 17,13.7 17,12 C17,10.3 15.7,9 14,9 Z';

export interface PoiIconDef {
  color: string;
  svgPath: string;
}

export const POI_ICON_DEFS: Record<string, PoiIconDef> = {
  'amenity-school': { color: '#3b82f6', svgPath: SVG_EDUCATION },
  'amenity-kindergarten': { color: '#3b82f6', svgPath: SVG_EDUCATION },
  'amenity-college': { color: '#3b82f6', svgPath: SVG_EDUCATION },
  'amenity-university': { color: '#3b82f6', svgPath: SVG_EDUCATION },
  'amenity-hospital': { color: '#ef4444', svgPath: SVG_HEALTH },
  'amenity-clinic': { color: '#ef4444', svgPath: SVG_HEALTH },
  'amenity-pharmacy': { color: '#ef4444', svgPath: SVG_HEALTH },
  'shop-supermarket': { color: '#f97316', svgPath: SVG_RETAIL },
  'shop-convenience': { color: '#f97316', svgPath: SVG_RETAIL },
  'shop-grocery': { color: '#f97316', svgPath: SVG_RETAIL },
  'shop-mall': { color: '#f97316', svgPath: SVG_RETAIL },
  'amenity-fuel': { color: '#eab308', svgPath: SVG_FUEL },
  'railway-station': { color: '#8b5cf6', svgPath: SVG_RAIL },
  'aeroway-aerodrome': { color: '#8b5cf6', svgPath: SVG_AIRPLANE },
  'amenity-bank': { color: '#10b981', svgPath: SVG_BANK },
  'amenity-atm': { color: '#10b981', svgPath: SVG_BANK },
  'leisure-park': { color: '#22c55e', svgPath: SVG_PARK },
  'office-government': { color: '#6b7280', svgPath: SVG_EDUCATION },
  'amenity-townhall': { color: '#6b7280', svgPath: SVG_EDUCATION },
  'tourism-attraction': { color: '#ec4899', svgPath: SVG_STAR },
  'tourism-museum': { color: '#ec4899', svgPath: SVG_STAR },
  'amenity-place_of_worship': { color: '#a78bfa', svgPath: SVG_WORSHIP },
  'poi-fallback': { color: '#9ca3af', svgPath: SVG_FALLBACK },
};

function createPoiIcon(map: MapLibreMap, name: string, color: string, svgPath: string): void {
  if (map.hasImage(name)) return;
  const SIZE = 28;
  const RADIUS = 6;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.warn(`[map-view-poi-icons] Failed to get 2d canvas context for POI icon "${name}"`);
    return;
  }

  // Rounded rectangle background
  ctx.beginPath();
  ctx.moveTo(RADIUS, 0);
  ctx.lineTo(SIZE - RADIUS, 0);
  ctx.quadraticCurveTo(SIZE, 0, SIZE, RADIUS);
  ctx.lineTo(SIZE, SIZE - RADIUS);
  ctx.quadraticCurveTo(SIZE, SIZE, SIZE - RADIUS, SIZE);
  ctx.lineTo(RADIUS, SIZE);
  ctx.quadraticCurveTo(0, SIZE, 0, SIZE - RADIUS);
  ctx.lineTo(0, RADIUS);
  ctx.quadraticCurveTo(0, 0, RADIUS, 0);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // White glyph — path coordinates are in the 28×28 space
  ctx.fillStyle = 'white';
  ctx.fill(new Path2D(svgPath));

  const imageData = ctx.getImageData(0, 0, SIZE, SIZE);
  map.addImage(name, { width: SIZE, height: SIZE, data: imageData.data });
}

export function registerPoiIcons(map: MapLibreMap): void {
  for (const [name, { color, svgPath }] of Object.entries(POI_ICON_DEFS)) {
    createPoiIcon(map, name, color, svgPath);
  }
}
